/**
 * Composant Pull-to-Refresh pour iOS PWA standalone
 *
 * Active la fonctionnalité pull-to-refresh sur les PWA iOS en mode standalone/fullscreen.
 * Sur iOS, cette fonctionnalité est désactivée par défaut pour les PWA, contrairement à Android
 * où elle fonctionne nativement.
 *
 * Ce composant utilise pulltorefreshjs pour réimplémenter le comportement de pull-to-refresh
 * avec une interface personnalisée.
 *
 * Fonctionnalités :
 * - Détection automatique du mode standalone (iOS PWA)
 * - Ne s'active que sur iOS PWA pour éviter les conflits avec le comportement natif
 * - Icônes SVG inline professionnelles
 * - Animations CSS fluides et centrées
 * - Cleanup automatique lors du démontage
 *
 * @module PullToRefresh
 * @author Équipe Brydge
 * @since 1.0.0
 * @requires pulltorefreshjs
 * @requires react
 */

import {useEffect} from 'react';
import PullToRefreshLib from 'pulltorefreshjs';
import {isIOS, isStandalone} from '../utils/detectPlatform';

/**
 * Hook personnalisé pour gérer le pull-to-refresh sur iOS PWA
 *
 * Active le pull-to-refresh uniquement si :
 * - L'appareil est iOS
 * - L'app est en mode standalone (installée)
 *
 * Sur Android et desktop, le comportement natif est conservé.
 *
 * @return {void}
 */
const usePullToRefresh = () => {
  useEffect(() => {
    // Vérifier si on est sur iOS en mode standalone
    const shouldEnablePTR = isIOS() && isStandalone();

    if (!shouldEnablePTR) {
      return;
    }

    // Configuration du pull-to-refresh
    const ptr = PullToRefreshLib.init({
      mainElement: 'body',

      // Distance minimale pour déclencher le refresh (en pixels)
      // Augmentée pour éviter les déclenchements accidentels
      distThreshold: 100,

      // Distance maximale possible (en pixels)
      distMax: 160,

      // Hauteur de l'élément après relâchement (en pixels)
      // Augmentée pour compenser le header fixe de 48px
      distReload: 124,

      // Distance avant de commencer à tirer (en pixels)
      distIgnore: 0,

      // Délai avant de déclencher onRefresh (en ms)
      refreshTimeout: 300,

      // Classes CSS personnalisées
      classPrefix: 'brydge-ptr--',

      // Fonction de résistance pour un mouvement naturel
      // Plus la valeur est haute, plus la résistance est forte
      resistanceFunction: (t) => Math.min(1, t / 2.5),

      // Condition pour activer le pull-to-refresh
      // Désactivé si :
      // - Pas en haut de la page (scrollY > 0)
      // - Un modal MUI est ouvert
      // - Un drawer MUI est ouvert
      // - Un dialog est ouvert
      shouldPullToRefresh: function() {
        // Vérifier si on est en haut de la page
        if (window.scrollY > 0) {
          return false;
        }

        // Desactiver sur les pages fullHeight (ex: reservations restaurant)
        // Ces pages gerent leur propre scroll interne via overflow: hidden
        // et le transform applique par PTR sur body casse le position: fixed
        // des elements comme la bottom nav et le mobile header
        const mainElement = document.querySelector('main');
        if (mainElement) {
          const computedOverflow = window.getComputedStyle(mainElement).overflow;
          if (computedOverflow === 'hidden') {
            return false;
          }
        }

        // Vérifier si un modal MUI est ouvert
        const modalBackdrop = document.querySelector('.MuiModal-backdrop, .MuiBackdrop-root');
        if (modalBackdrop) {
          return false;
        }

        // Vérifier si un drawer MUI est ouvert
        const drawer = document.querySelector('.MuiDrawer-root .MuiPaper-root');
        if (drawer) {
          return false;
        }

        // Vérifier si un dialog MUI est ouvert
        const dialog = document.querySelector('.MuiDialog-root');
        if (dialog) {
          return false;
        }

        // Vérifier si body a aria-hidden (souvent utilisé quand un modal est ouvert)
        if (document.body.getAttribute('aria-hidden') === 'true') {
          return false;
        }

        // Vérifier si body a une classe indiquant qu'un overlay est ouvert
        if (document.body.classList.contains('modal-open')) {
          return false;
        }

        return true;
      },

      // Markup HTML personnalisé pour l'interface avec styles inline pour le header fixe
      getMarkup: () => `
        <div class="__PREFIX__box" style="margin-top: 48px;">
          <div class="__PREFIX__content">
            <div class="__PREFIX__icon"></div>
            <div class="__PREFIX__text"></div>
          </div>
        </div>
      `,

      // Styles CSS personnalisés intégrés
      getStyles: () => `
        .__PREFIX__ptr {
          pointer-events: none;
          font-size: 0.85em;
          font-weight: 500;
          top: 48px;
          height: 0;
          transition: height 0.3s, min-height 0.3s;
          text-align: center;
          width: 100%;
          overflow: hidden;
          display: flex;
          align-items: flex-end;
          align-content: stretch;
          z-index: 999;
          background: linear-gradient(to bottom, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0) 100%);
        }

        .__PREFIX__box {
          padding: 10px;
          flex-basis: 100%;
        }

        .__PREFIX__pull {
          transition: none;
        }

        .__PREFIX__text {
          margin-top: 0.5em;
          color: rgba(0, 0, 0, 0.7);
          font-size: 14px;
          font-weight: 500;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
            'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
        }

        .__PREFIX__icon {
          display: flex;
          align-items: center;
          justify-content: center;
          transition: transform 0.3s ease;
        }

        .__PREFIX__icon svg {
          display: block;
        }

        .__PREFIX__release .__PREFIX__icon {
          transform: rotate(180deg);
        }

        @keyframes __PREFIX__rotate {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        .__PREFIX__refresh .__PREFIX__icon {
          animation: __PREFIX__rotate 1.2s linear infinite;
        }

        .__PREFIX__refresh .__PREFIX__icon svg {
          transform-origin: center center;
        }
      `,

      // Icône SVG inline pour "Tirer pour rafraîchir" (flèche vers le bas)
      iconArrow: `
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 5v14m6-8l-6-6m-6 6l6-6"/></svg>
      `,

      // Icône SVG inline pendant le rafraîchissement (rotation animée via CSS)
      iconRefreshing: `
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
        <g fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2">
        <path d="M19.933 13.041a8 8 0 1 1-9.925-8.788c3.899-1 7.935 1.007 9.425 4.747"/>
        <path d="M20 4v5h-5"/>
        </g>
        </svg>
      `,

      // Textes d'instructions
      instructionsPullToRefresh: 'Tirez pour rafraîchir',
      instructionsReleaseToRefresh: 'Relâchez pour rafraîchir',
      instructionsRefreshing: 'Rafraîchissement...',

      // Callback de rafraîchissement
      onRefresh: () => {
        // Recharge la page complète
        window.location.reload();
      },
    });

    // Cleanup lors du démontage du composant
    return () => {
      if (ptr && typeof ptr.destroy === 'function') {
        ptr.destroy();
      }
    };
  }, []);
};

/**
 * Composant PullToRefresh
 *
 * Composant React qui active le pull-to-refresh sur iOS PWA.
 * Ne rend rien visuellement (null), la logique est entièrement gérée
 * par le hook usePullToRefresh et pulltorefreshjs.
 *
 * @component
 * @return {null} Ne rend aucun élément visible
 *
 * @example
 * // Dans App.js
 * import PullToRefresh from './pwa/components/PullToRefresh';
 *
 * function App() {
 *   return (
 *     <div>
 *       <PullToRefresh />
 *       {/* Reste de l'application *\/}
 *     </div>
 *   );
 * }
 */
const PullToRefresh = () => {
  usePullToRefresh();
  return null;
};

export default PullToRefresh;
